import React from 'react';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListSubheader from '@material-ui/core/ListSubheader';
import DashboardIcon from '@material-ui/icons/Dashboard';
import SchoolIcon from '@material-ui/icons/School';
import OfflineBoltIcon from '@material-ui/icons/OfflineBolt';
import SettingsIcon from '@material-ui/icons/Settings';
import BusinessIcon from '@material-ui/icons/Business';
import PostAddIcon from '@material-ui/icons/PostAdd';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import GroupWorkIcon from '@material-ui/icons/GroupWork';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';

import { OrganizationType } from 'store/modules/auth/auth';
import RouterProvider from 'components/RouterProvider/RouterProvider';
import LanguageSwitcher from 'components/LanguageSwitcher/LanguageSwitcher';
import { useFormatMessage } from 'components/IntlProvider';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import LogoutButton from './LogoutButton';

const { connect, selectors } = StoreProvider;
const { Link, ROUTES } = RouterProvider;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    subheader: {
      fontWeight: 700,
      opacity: 0.825,
      fontSize: '0.825rem',
      [theme.breakpoints.down('md')]: {
        fontSize: '0.725rem',
        lineHeight: '2rem',
      },
    },
    primary: {
      fontSize: '0.825rem',
      [theme.breakpoints.up('md')]: {
        fontSize: '0.825rem',
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: '0.9rem',
      },
      [theme.breakpoints.up('xl')]: {
        fontSize: '1rem',
      },
    },
    item: {
      paddingBottom: '4px',
      paddingTop: '4px',
      [theme.breakpoints.up('md')]: {
        paddingBottom: 0,
        paddingTop: 0,
      },
      [theme.breakpoints.up('lg')]: {
        paddingBottom: '4px',
        paddingTop: '4px',
      },
      [theme.breakpoints.up('xl')]: {
        paddingBottom: '8px',
        paddingTop: '8px',
      },
    },
  })
);

export const MainMenu = () => {
  const t = useFormatMessage();
  const classes = useStyles();
  return (
    <div>
      <Link to={ROUTES.DASHBOARD}>
        <ListItem
          button
          classes={{
            root: classes.item,
          }}
        >
          <ListItemIcon>
            <DashboardIcon />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.primary,
            }}
            primary={t('dashboard.menu.dashboard', 'Dashboard')}
          />
        </ListItem>
      </Link>

      <Link to={ROUTES.PROJECTS.CREATE}>
        <ListItem
          button
          classes={{
            root: classes.item,
          }}
        >
          <ListItemIcon>
            <PostAddIcon />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.primary,
            }}
            primary={t('dashboard.menu.addproject', 'New Project')}
          />
        </ListItem>
      </Link>
    </div>
  );
};

export const WorkbenchMenu = () => {
  const t = useFormatMessage();
  const classes = useStyles();
  return (
    <div>
      <ListSubheader
        inset
        className={classes.subheader}
        color="primary"
        disableSticky
      >
        {t('dashboard.menu.workbench', 'Workbench')}
      </ListSubheader>
      <Link to={ROUTES.WORKBENCH.INNOVATION}>
        <ListItem
          button
          classes={{
            root: classes.item,
          }}
        >
          <ListItemIcon>
            <OfflineBoltIcon />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.primary,
            }}
            primary={t('dashboard.menu.innovation', 'Innovation')}
          />
        </ListItem>
      </Link>

      <Link to={ROUTES.WORKBENCH.EXPERIENCE}>
        <ListItem
          button
          classes={{
            root: classes.item,
          }}
        >
          <ListItemIcon>
            <SchoolIcon />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.primary,
            }}
            primary={t('dashboard.menu.experience', 'Experience')}
          />
        </ListItem>
      </Link>

      <Link to={ROUTES.WORKBENCH.BRANDING}>
        <ListItem
          button
          classes={{
            root: classes.item,
          }}
        >
          <ListItemIcon>
            <BusinessIcon />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.primary,
            }}
            primary={t('dashboard.menu.branding', 'Branding')}
          />
        </ListItem>
      </Link>
    </div>
  );
};

export const UserMenu = connect((state) => ({
  name: selectors.authSelectors.userDisplayName(state),
}))(({ name }: { name: string }) => {
  const t = useFormatMessage();
  const classes = useStyles();
  return (
    <div>
      <ListSubheader
        inset
        className={classes.subheader}
        color="primary"
        disableSticky
      >
        {name}
      </ListSubheader>
      <Link to={ROUTES.SETTINGS.MAIN}>
        <ListItem
          button
          classes={{
            root: classes.item,
          }}
        >
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.primary,
            }}
            primary={t('dashboard.menu.myprofile', 'Profile')}
          />
        </ListItem>
      </Link>
      <LogoutButton />
    </div>
  );
});

export const UserOrganizationMenu = connect((state) => ({
  organization: selectors.authSelectors.userOrganization(state),
  isOrganizationAdmin: selectors.authSelectors.isOrganizationAdmin(state),
}))(({ organization }: { organization: OrganizationType | undefined }) => {
  const classes = useStyles();

  if (!organization) {
    return null;
  }
  return (
    <div>
      <ListSubheader
        inset
        className={classes.subheader}
        color="primary"
        disableSticky
      >
        {organization.name}
      </ListSubheader>
    </div>
  );
});

export const DeepTrueAdminMenu = () => {
  const t = useFormatMessage();
  const classes = useStyles();
  return (
    <div>
      <ListSubheader
        inset
        className={classes.subheader}
        color="primary"
        disableSticky
      >
        {t('dashboard.menu.deeptrueadmin', 'DeepTrue Admin')}
      </ListSubheader>
      <Link to={ROUTES.ADMIN.ORGANIZATIONS.LIST}>
        <ListItem
          button
          classes={{
            root: classes.item,
          }}
        >
          <ListItemIcon>
            <GroupWorkIcon />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.primary,
            }}
            primary={t('dashboard.menu.organizations', 'Organizations')}
          />
        </ListItem>
      </Link>
      <Link to={ROUTES.ADMIN.TEMPLATES.SURVEYS.MAIN}>
        <ListItem
          button
          classes={{
            root: classes.item,
          }}
        >
          <ListItemIcon>
            <FormatListBulletedIcon />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.primary,
            }}
            primary={t('dashboard.menu.templates.survey', 'Survey Templates')}
          />
        </ListItem>
      </Link>
      <Link to={ROUTES.ADMIN.TEMPLATES.BRIEFINGS.MAIN}>
        <ListItem
          button
          classes={{
            root: classes.item,
          }}
        >
          <ListItemIcon>
            <FormatListBulletedIcon />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.primary,
            }}
            primary={t(
              'dashboard.menu.templates.briefing',
              'Briefing Templates'
            )}
          />
        </ListItem>
      </Link>
      <Link to={ROUTES.ADMIN.PRODUCTS.LIST}>
        <ListItem
          button
          classes={{
            root: classes.item,
          }}
        >
          <ListItemIcon>
            <FormatListBulletedIcon />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.primary,
            }}
            primary={t('dashboard.menu.products', 'Products')}
          />
        </ListItem>
      </Link>
      <Link to={ROUTES.ADMIN.WORKBENCHES.LIST}>
        <ListItem
          button
          classes={{
            root: classes.item,
          }}
        >
          <ListItemIcon>
            <FormatListBulletedIcon />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.primary,
            }}
            primary={t('dashboard.menu.workbenches', 'Workbenches')}
          />
        </ListItem>
      </Link>
    </div>
  );
};

export const LanguageMenu = ({ isMenuOpen }: { isMenuOpen: boolean }) => (
  <div style={{ marginTop: 'auto' }}>
    <ListItem style={{ display: isMenuOpen ? 'block' : 'none' }}>
      <LanguageSwitcher />
    </ListItem>
  </div>
);
